import React, { useState } from "react";
import { RefreshCw } from "lucide-react";

function RecommendedList({
  generatedCombinations,
  onSelect,
  selectedModule,
  topPickIndex,
  budget,
  onRefresh, 
}) {
  const [expandedIndex, setExpandedIndex] = useState(null);

  //calculations on all metrics 
  const getAllMetrics = (combo) => {
    const total = combo.total;
    const count = combo.items.length;
    const safeBudget = budget || 1; 

    // 1. Max Budget Metric
    const usedPercent = (total / safeBudget) * 100;
    
    // 2. Economic Metric
    const saved = safeBudget - total;

    // 3. Variation Metric
    const uniqueCats = new Set(combo.items.map(i => i.category)).size;

    return [
      {
        id: "max_budget",
        label: "Budget Used",
        value: `${usedPercent.toFixed(1)}%`,
        color: "bg-blue-50 text-blue-700 border-blue-200",
      },
      {
        id: "economic",
        label: "Balance",
        value: `RM ${saved.toFixed(2)}`,
        color: "bg-green-50 text-green-700 border-green-200",
      },
      {
        id: "variation",
        label: "Variety",
        value: `${uniqueCats} Types`,
        color: "bg-purple-50 text-purple-700 border-purple-200",
      },
      {
        id: "best_value",
        label: "Quantity",
        value: `${count} Items`,
        color: "bg-red-50 text-red-700 border-red-200",
      }
    ];
  };

  if (!generatedCombinations || generatedCombinations.length === 0) {
    return (
      <div className="bg-[#FFF5E4] border border-[#FFD8A6] p-6 rounded-xl text-center">
        <p className="text-[#7A6248] font-semibold">
          No grocery combinations generated yet.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-[#FFF7E6] border border-[#FFD8A6] rounded-2xl p-6 shadow space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <h2 className="text-2xl font-bold text-[#5A3E2B]">
          Grocery Combinations
        </h2>

        {/* REFRESH BUTTON (Only for the active filter) */}
        <button
          onClick={onRefresh} // Re-fetches current active module
          className="flex items-center gap-2 px-3 py-1.5 
            bg-white border border-[#E8D1B7] rounded-lg 
            text-[#5A3E2B] text-sm font-bold 
            shadow-sm hover:bg-[#FFF0D9] active:scale-95 transition"
          title="Refresh Combination"
        >
          <RefreshCw size={18} />
        </button>
      </div>


      {generatedCombinations.map((combo, index) => {
        const isExpanded = expandedIndex === index;
        const metrics = getAllMetrics(combo);

        return (
          <div
            key={index}
            className={`
              bg-white border rounded-xl shadow-sm overflow-hidden transition-all duration-200
              ${topPickIndex === index ? "border-[#38A3A5] ring-1 ring-[#38A3A5]" : "border-[#E8D1B7]"}
            `}
          >
            {/* --- HEADER --- */}
            <div
              onClick={() => setExpandedIndex(isExpanded ? null : index)}
              className="cursor-pointer flex justify-between items-center p-4 hover:bg-[#FFF7E6] transition"
            >
              <div>
                <h3 className="font-bold text-lg">Option {index + 1}</h3>
                <p className="text-sm text-[#7A6248]">
                  Total: RM {combo.total.toFixed(2)}
                </p>
              </div>

              <div className="flex items-center gap-3">
                {topPickIndex === index && (
                  <span className="px-3 py-1 text-xs rounded-full bg-[#38A3A5] text-white font-semibold">
                    TOP PICK
                  </span>
                )}
                <span className="text-xl">{isExpanded ? "▲" : "▼"}</span>
              </div>
            </div>

            {/* --- EXPANDED CONTENT --- */}
            {isExpanded && (
              <div className="p-4 bg-[#FFFDF8] border-t border-[#E8D1B7] space-y-3">
                
                {/* 1. THE ITEM LIST */}
                <div className="space-y-3 mb-4">
                  {combo.items.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm bg-white p-3 rounded-lg border shadow-sm">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-white rounded-lg border p-1 flex items-center justify-center overflow-hidden shrink-0">
                          <img
                            src={item.image_url}
                            alt={item.name}
                            className="w-full h-full object-contain"
                            onError={(e) => { e.target.src = "https://cdn-icons-png.flaticon.com/512/2662/2662503.png"; }}
                          />
                        </div>
                        <span className="font-medium text-gray-700 max-w-[160px] sm:max-w-[200px] leading-tight">
                          {item.name || item.product_name}
                        </span>
                      </div>
                      <span className="font-bold text-[#38A3A5] whitespace-nowrap ml-2">
                        RM {item.price.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>

                {/* --- 2. THE METRIC GRID (2x2 on Mobile, 4x1 on Desktop) --- */}
                <div className="pt-2 border-t border-dashed border-[#E8D1B7]">
                  <p className="text-xs font-bold text-[#7A6248] mb-2 uppercase tracking-wider">Analysis</p>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {metrics.map((m) => (
                      <div 
                        key={m.id} 
                        className={`
                          flex flex-col items-center justify-center 
                          py-1 px-1 rounded-lg border text-center transition-all
                          ${m.color} 
                        `}
                      >
                         {/* Added Label in one row for compact layout */}
                        <div className="flex items-center gap-1 mb-0.5">
                           <span className="text-[10px] uppercase font-bold tracking-wider opacity-70">
                             {m.label}
                           </span>
                        </div>
                        <span className="text-sm font-extrabold leading-none">{m.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* --- 3. SELECT BUTTON --- */}
                <button
                  onClick={() => onSelect(combo.items)}
                  className="w-full mt-3 py-3 rounded-xl bg-[#38A3A5] text-white font-bold shadow-md hover:brightness-110 active:scale-95 transition"
                >
                  Use This Combination
                </button>

              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default RecommendedList;